import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './Modal.module.scss';
import Close from '../../Icons/Close';

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.97 },
};

const Modal = ({
  children,
  show,
  onClose,
  size = 'default',
  title,
  showActions = false,
  onConfirm,
  confirmLabel = 'Confirm',
  confirmColor = '#EF4444',
  cancelLabel = 'Cancel',
}) => {
  const modalRef = useRef();
  const bodyRef = useRef();

  const handleBodyWheelCapture = (event) => {
    const bodyEl = bodyRef.current;
    if (!bodyEl) return;
    const isVertical = Math.abs(event.deltaY) > Math.abs(event.deltaX);
    if (!isVertical) return;
    bodyEl.scrollTop += event.deltaY;
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.body.classList.add('no-scroll');
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.body.classList.remove('no-scroll');
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onClose]);

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          className={styles.overlay}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={overlayVariants}
          transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
        >
          <motion.div
            ref={modalRef}
            className={
              styles.modal +
              (size === 'small' ? ' ' + styles.modalSmall : '') +
              (size === 'dialog' ? ' ' + styles.modalDialog : '')
            }
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className={styles.header}>
              {title && <h3 className={styles.title}>{title}</h3>}
              <button onClick={onClose} className={styles.closeButton}>
                <Close fill="#404040" />
              </button>
            </div>
            <div
              ref={bodyRef}
              className={styles.body}
              onWheelCapture={handleBodyWheelCapture}
            >
              {children}
            </div>
            {showActions && (
              <div className={styles.actions}>
                <button onClick={onClose} className={styles.cancelButton}>
                  {cancelLabel}
                </button>
                <button onClick={onConfirm} className={styles.confirmButton}>
                  {confirmLabel}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Modal;
